import { useFeatureValue } from '@growthbook/growthbook-react'
import { useRouter } from 'next/router'

import { useAuth } from './useAuth'
import { useCart } from '../components/Cart/CartProvider'
import useExchangeRate from './useExchangeRate'
import { CalculateTakeRate } from '../lib/resources/price'
import { getCartMaxPrice } from '../lib/cart'
import { getCountryCurrency } from '../lib/product'
import { getCountryAndLocaleStrings } from '../utils/locales'
import { calculateTakeRate } from '../lib/calculateTakeRate'
import { Product } from '../utils/types/Product'

/**
 * Return a function that modifies the prices of the product to tweak take rate based
 * on selected quantity
 */
const useTakeRate = (): CalculateTakeRate => {
  const { consumptionTier } = useAuth()
  const { modifiedCart } = useCart()
  const { locale: countryAndLocaleString } = useRouter()
  const { country } = getCountryAndLocaleStrings(countryAndLocaleString)
  const currentCurrencySymbol = getCountryCurrency(country)
  const exchangeRate = useExchangeRate(currentCurrencySymbol)
  const isTakeRateEnabled = useFeatureValue('consumption-based-take-rate', true)

  /**
   * @param product product to modify prices for
   * @param selectedQuantity to take current product value into account
   * @param excludeAmount adjust total used, to exclude or include cart / extra products etc.
   */
  return (
    product: Product,
    selectedQuantity: number,
    excludeAmount?: number,
  ) => {
    const cartTotal = modifiedCart ? getCartMaxPrice(modifiedCart) : 0

    return calculateTakeRate({
      product,
      selectedQuantity,
      consumptionTier: consumptionTier ?? 0,
      cartTotal,
      exchangeRate: exchangeRate || 1,
      isTakeRateEnabled,
      excludeAmount,
    })
  }
}

export default useTakeRate
